import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Sparkles } from 'lucide-react';

const API_URL = '';

/**
 * NewReviewersCarousel - Compact scrolling list of new reviewers
 * 
 * Mobile-optimized showcase of new reviewers.
 * Shows display name + username on a single line, 5 visible at a time.
 * Smooth continuous scroll that seamlessly loops.
 * Only clickable if reviewer is LIVE.
 */
const NewReviewersCarousel = ({ onSelectReviewer }) => {
  const [newReviewers, setNewReviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef(null);
  const animationRef = useRef(null);
  const scrollPositionRef = useRef(0);
  
  // Fetch new reviewers on mount
  useEffect(() => {
    const fetchNewReviewers = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/reviewers/new?days=14&limit=20`);
        setNewReviewers(response.data.reviewers || []);
      } catch (error) {
        console.error('Error fetching new reviewers:', error);
        setNewReviewers([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchNewReviewers();
  }, []);
  
  // Smooth continuous scroll animation using requestAnimationFrame
  useEffect(() => {
    if (newReviewers.length <= 5 || !scrollRef.current) return;
    
    const container = scrollRef.current;
    const lineHeight = 28;
    const singleListHeight = newReviewers.length * lineHeight;
    const scrollSpeed = 0.3; // Pixels per frame - slower = smoother
    
    const animate = () => {
      if (!isPaused) {
        scrollPositionRef.current += scrollSpeed;
        
        // Seamless loop: when we've scrolled past the first copy, reset to 0
        if (scrollPositionRef.current >= singleListHeight) {
          scrollPositionRef.current = 0;
        }
        
        container.scrollTop = scrollPositionRef.current;
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [newReviewers.length, isPaused]);
  
  if (loading) {
    return (
      <div className="animate-pulse bg-yellow-50 rounded-lg p-3 mb-4">
        <div className="h-3 bg-yellow-100 rounded w-1/3 mb-2"></div>
        <div className="space-y-1">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-4 bg-yellow-100 rounded w-2/3"></div>
          ))}
        </div>
      </div>
    );
  }
  
  if (newReviewers.length === 0) {
    return null;
  }
  
  // Duplicate the list for seamless looping
  const displayReviewers = newReviewers.length > 5 
    ? [...newReviewers, ...newReviewers] 
    : newReviewers;
  
  const ReviewerRow = ({ reviewer }) => {
    const isLive = reviewer.is_live;
    
    return (
      <div
        onClick={() => isLive && onSelectReviewer?.(reviewer)}
        className={`flex items-center gap-2 py-1 px-2 rounded transition-colors ${
          isLive 
            ? 'cursor-pointer hover:bg-yellow-100 active:bg-yellow-200' 
            : 'cursor-default'
        }`}
        style={{ height: '28px' }}
      >
        {/* LIVE indicator */}
        {isLive && (
          <span className="flex-shrink-0 w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
        )}
        
        {/* Name + Username on one line */}
        <span className={`text-sm truncate ${isLive ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
          {reviewer.name}
        </span>
        <span className="text-xs text-gray-400 truncate flex-shrink-0">
          @{reviewer.username}
        </span>
        
        {/* NEW badge */}
        {reviewer.isNew && !isLive && (
          <span className="flex-shrink-0 text-[8px] font-bold text-green-600 bg-green-100 px-1 rounded">
            NEW
          </span>
        )}
      </div>
    );
  };
  
  return (
    <div 
      className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-lg p-3 mb-4"
      data-testid="new-reviewers-carousel"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setTimeout(() => setIsPaused(false), 2000)}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-yellow-600" />
          <span className="text-xs font-semibold text-gray-800">New Reviewers This Week</span>
        </div>
        <span className="text-[10px] text-gray-500 bg-white/80 px-1.5 py-0.5 rounded-full">
          {newReviewers.length} new
        </span>
      </div>
      
      {/* Subtitle */}
      <p className="text-[10px] text-gray-500 mb-2">
        New reviewers give extra attention. Be their first!
      </p>
      
      {/* Scrolling List - 5 visible lines */}
      <div 
        ref={scrollRef}
        className="overflow-hidden"
        style={{ 
          height: `${5 * 28}px`,
          scrollBehavior: 'auto' // Disable CSS smooth scroll, we handle it manually
        }}
      >
        <div className="space-y-0">
          {displayReviewers.map((reviewer, index) => (
            <ReviewerRow 
              key={`${reviewer.id}-${index}`} 
              reviewer={reviewer} 
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewReviewersCarousel;
